import { useEffect, useMemo, useState } from 'react';

import { getGrowlersCartRules } from 'services/firebase/growlersCartRulesService';
import {
  applyGrowlersCartRules,
  type GrowlersCartPricing,
} from 'utils/growlersCartPricing';
import {
  DEFAULT_GROWLERS_CART_RULES,
  type GrowlersCartRules,
} from 'utils/types/GrowlersCartRules';

interface UseGrowlersCartPricingResult extends GrowlersCartPricing {
  loading: boolean;
  rules: GrowlersCartRules;
}

export const useGrowlersCartPricing = (
  itemCount: number,
  subtotal: number,
  enabled = true,
): UseGrowlersCartPricingResult => {
  const [rules, setRules] = useState<GrowlersCartRules>(
    DEFAULT_GROWLERS_CART_RULES,
  );
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const loadRules = async () => {
      const nextRules = await getGrowlersCartRules();
      if (!cancelled) {
        setRules(nextRules);
        setLoading(false);
      }
    };

    void loadRules();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const pricing = useMemo(
    () => applyGrowlersCartRules({ itemCount, subtotal, rules }),
    [itemCount, rules, subtotal],
  );

  return {
    loading,
    rules,
    ...pricing,
  };
};
